import React from 'react';
import { formatDateTime, formatTime } from '../shared/dateTimeFormatting';
import type { AlertWorkflowProgress, AlertWorkflowProgressEntry, AlertWorkflowProgressInstance } from '../services/tenantApi';
import './WorkflowProgressPanel.css';

export type WorkflowProgressSubscriber = (callback: (payload: { alertId?: string }) => void) => () => void;

interface WorkflowProgressPanelProps {
  alertId: string;
  loadProgress: (alertId: string) => Promise<AlertWorkflowProgress>;
  subscribe?: WorkflowProgressSubscriber;
  refreshKey?: unknown;
}

const isActive = (status?: string | null) => /^(running|pending|pendingscheduling)$/i.test(status || '');
const sameId = (left?: string | null, right?: string | null) => !!left && !!right && left.toLowerCase() === right.toLowerCase();

export function formatRemaining(ms: number): string {
  if (ms <= 0) return 'resuming…';
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `in ${minutes}m ${String(seconds).padStart(2, '0')}s` : `in ${seconds}s`;
}

const statusIcon = (status: string) => {
  const value = status.toLowerCase();
  if (value === 'completed') return '✓';
  if (value === 'failed') return '✕';
  if (value === 'cancelled') return '⊘';
  return '●';
};

export const WorkflowProgressPanel: React.FC<WorkflowProgressPanelProps> = ({ alertId, loadProgress, subscribe, refreshKey }) => {
  const [progress, setProgress] = React.useState<AlertWorkflowProgress | null>(null);
  const [error, setError] = React.useState('');
  const [now, setNow] = React.useState(() => Date.now());
  const requestRef = React.useRef(0);

  const refresh = React.useCallback(async () => {
    const request = ++requestRef.current;
    try {
      const next = await loadProgress(alertId);
      if (request !== requestRef.current) return;
      setProgress(next);
      setError('');
    } catch {
      if (request === requestRef.current) setError('Workflow progress is unavailable.');
    }
  }, [alertId, loadProgress]);

  React.useEffect(() => {
    setProgress(null);
    setError('');
  }, [alertId]);

  React.useEffect(() => {
    void refresh();
  }, [refresh, refreshKey]);

  const subscribeRef = React.useRef(subscribe);
  subscribeRef.current = subscribe;
  const canSubscribe = !!subscribe;

  React.useEffect(() => {
    const subscribeNow = subscribeRef.current;
    if (!canSubscribe || !subscribeNow) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const dispose = subscribeNow(payload => {
      if (!sameId(payload?.alertId, alertId)) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void refresh(), 300);
    });
    return () => {
      if (timer) clearTimeout(timer);
      dispose();
    };
  }, [alertId, refresh, canSubscribe]);

  const instances = progress?.instances ?? [];
  const anyActive = instances.some(instance => isActive(instance.status) || instance.entries.some(entry => isActive(entry.status)));
  const hasCountdown = instances.some(instance => instance.entries.some(entry => isActive(entry.status) && entry.resumesAt));

  React.useEffect(() => {
    if (!anyActive) return undefined;
    const poll = setInterval(() => void refresh(), 30000);
    return () => clearInterval(poll);
  }, [anyActive, refresh]);

  React.useEffect(() => {
    if (!hasCountdown) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [hasCountdown]);

  if (!progress && !error) return null;
  if (!error && instances.length === 0) return null;

  const children = (parent: AlertWorkflowProgressInstance) => instances.filter(instance => sameId(instance.parentInstanceId, parent.instanceId));
  const roots = instances.filter(instance => !instance.parentInstanceId || !instances.some(other => sameId(other.instanceId, instance.parentInstanceId)));

  const renderEntry = (entry: AlertWorkflowProgressEntry, instance: AlertWorkflowProgressInstance) => {
    const status = entry.status || 'Pending';
    const active = isActive(status);
    const at = active ? entry.startedAt : entry.completedAt || entry.startedAt;
    const resumesAt = active && entry.resumesAt ? new Date(entry.resumesAt) : null;
    const nested = children(instance).filter(child => sameId(child.parentStepId, entry.stepId));
    return (
      <li key={`${instance.instanceId}-${entry.stepId}`} className={`workflow-progress-entry is-${status.toLowerCase()}`}>
        <span className="workflow-progress-icon" aria-hidden="true">{statusIcon(status)}</span>
        <div className="workflow-progress-body">
          <div className="workflow-progress-line">
            {at && <time className="workflow-progress-time" dateTime={at}>{formatTime(at, { second: '2-digit' })}</time>}
            <span className="workflow-progress-message">{entry.message}</span>
          </div>
          {resumesAt && !Number.isNaN(resumesAt.getTime()) && (
            <div className="workflow-progress-meta">
              Resumes {formatTime(resumesAt.toISOString(), { second: '2-digit' })} ({formatRemaining(resumesAt.getTime() - now)})
            </div>
          )}
          {entry.error && status.toLowerCase() === 'failed' && <div className="workflow-progress-error">{entry.error}</div>}
          {nested.map(child => renderInstance(child, true))}
        </div>
      </li>
    );
  };

  const renderInstance = (instance: AlertWorkflowProgressInstance, nested = false): React.ReactNode => {
    const orphanChildren = children(instance).filter(child => !instance.entries.some(entry => sameId(entry.stepId, child.parentStepId)));
    return (
      <div key={instance.instanceId} className={`workflow-progress-instance${nested ? ' is-nested' : ''}`}
        aria-label={`${instance.workflowName} progress`}>
        <div className="workflow-progress-instance-header">
          <span className="workflow-progress-name">{nested ? `↳ ${instance.workflowName}` : instance.workflowName}</span>
          <span className={`workflow-progress-status is-${(instance.status || 'pending').toLowerCase()}`}>{instance.status || 'Pending'}</span>
          {!nested && instance.startedAt && <span className="workflow-progress-started">{formatDateTime(instance.startedAt)}</span>}
        </div>
        {instance.entries.length === 0 && isActive(instance.status) && (
          <p className="workflow-progress-empty">Waiting for the first step to start…</p>
        )}
        <ol className="workflow-progress-list">{instance.entries.map(entry => renderEntry(entry, instance))}</ol>
        {instance.errorMessage && instance.status?.toLowerCase() === 'failed' && (
          <div className="workflow-progress-error">{instance.errorMessage}</div>
        )}
        {orphanChildren.map(child => renderInstance(child, true))}
      </div>
    );
  };

  return (
    <section className="workflow-progress-section" aria-label="Workflow progress">
      <h4>
        Workflow Progress
        {anyActive && <span className="workflow-progress-live" title="Updates live as steps run">Live</span>}
      </h4>
      {error && <p className="workflow-progress-error" role="status">{error}</p>}
      {roots.map(instance => renderInstance(instance))}
    </section>
  );
};
